import React, { useEffect, useState } from 'react';
import { Clock, Lock, Unlock, Hourglass } from 'lucide-react';

/** Countdown that ticks every second until `targetIso`. */
const useCountdown = (targetIso) => {
  const [ms, setMs] = useState(() => Math.max(0, new Date(targetIso).getTime() - Date.now()));
  useEffect(() => {
    if (!targetIso) return;
    const it = setInterval(() => {
      setMs(Math.max(0, new Date(targetIso).getTime() - Date.now()));
    }, 1000);
    return () => clearInterval(it);
  }, [targetIso]);
  const s = Math.floor(ms / 1000);
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  return { days, hours, minutes, seconds, expired: ms === 0 };
};

const pad = (n) => String(n).padStart(2, '0');

const MarketStatusCard = ({ status }) => {
  // status = { market_open, time_locked, force_locked, awaiting_results, next_lock_at, next_unlock_at, match_day }
  const targetIso = status.market_open ? status.next_lock_at : status.next_unlock_at;
  const { days, hours, minutes, seconds, expired } = useCountdown(targetIso);
  const open = status.market_open;
  const awaiting = !!status.awaiting_results;

  // Awaiting results = jogo já aconteceu, admin a lançar dados.
  // Mostra um aviso dedicado em vez do contador habitual.
  if (awaiting) {
    return (
      <div className="rounded-2xl p-5 border bg-sky-500/10 border-sky-400/30" data-testid="market-status-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 shrink-0 rounded-xl bg-sky-500/20 border border-sky-400/30 flex items-center justify-center">
            <Hourglass className="w-5 h-5 text-sky-300 animate-pulse" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] font-black uppercase tracking-widest text-sky-300">Resultados sendo processados</div>
            <div className="text-white font-black text-lg mt-0.5">
              {status.match_day ? status.match_day.label : 'Jornada em curso'}
            </div>
            <div className="text-xs text-sky-100/80 mt-1">
              A jornada fechou. Pontos em breve — o admin está a lançar os dados do jogo.
            </div>
            {status.match_day && (
              <div className="text-[11px] text-slate-400 mt-1">Data do jogo: {status.match_day.date}</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-2xl p-5 border ${open ? 'bg-emerald-500/10 border-emerald-400/30' : 'bg-amber-500/10 border-amber-400/30'}`} data-testid="market-status-card">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {open
              ? <Unlock className="w-4 h-4 text-emerald-400" />
              : <Lock className="w-4 h-4 text-amber-400" />}
            <span className={`text-xs font-black uppercase tracking-widest ${open ? 'text-emerald-300' : 'text-amber-300'}`}>
              {open ? 'Mercado Aberto' : 'Mercado Fechado'}
            </span>
          </div>
          <div className="text-white font-black text-lg mt-1">
            {status.match_day ? status.match_day.label : 'Nenhuma jornada activa'}
          </div>
          {status.match_day && (
            <div className="text-xs text-slate-400 mt-0.5">Data: {status.match_day.date}</div>
          )}
          {status.force_locked && (
            <div className="text-[11px] text-red-300 mt-1">Fechado manualmente pelo admin</div>
          )}
        </div>
        {targetIso && !expired && (
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center justify-end gap-1">
              <Clock className="w-3 h-3" /> {open ? 'Fecha em' : 'Abre em'}
            </div>
            <div className="text-white font-mono font-black text-lg leading-tight" data-testid="market-countdown">
              {days > 0 && <span>{days}d </span>}
              {pad(hours)}:{pad(minutes)}:{pad(seconds)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MarketStatusCard;
